import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Paperclip, Send, FileText, CalendarClock, FileSignature, ArrowLeft, Download, Check, X } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { CompanyAvatar } from "@/components/CompanyAvatar";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, fmtDateTime, money, download } from "@/lib/api";

function Bubble({ m, mine, onRespond }) {
  const meta = m.meta || {};
  const status = meta.status;
  const actionable = !mine && status === "pending";
  const box = `max-w-[85%] sm:max-w-[70%] rounded-2xl px-4 py-3 ${mine ? "ml-auto bg-[#E0218A]/15 border border-[#E0218A]/30" : "bg-[#FBE9F3] border border-[#2A1240]/10"}`;
  return (
    <div className={box} data-testid={`message-${m.id}`}>
      <p className="text-[11px] text-slate-400 mb-1">{m.sender_name} · {fmtDateTime(m.created_at)}</p>
      {m.kind === "text" && <p className="text-sm text-[#2A1240] whitespace-pre-line">{m.body}</p>}
      {m.kind === "attachment" && (
        <button onClick={() => download(`/messages/files/${meta.file_id}`, meta.title)} className="flex items-center gap-2 text-sm text-[#8E2BC4] hover:underline" data-testid={`attachment-download-${m.id}`}>
          <Download className="h-4 w-4" />{meta.title}
        </button>
      )}
      {m.kind === "proposal" && (
        <div>
          <p className="kicker flex items-center gap-1.5"><FileText className="h-3.5 w-3.5" />Proposal</p>
          <p className="mt-2 font-display text-lg text-[#2A1240]">{meta.title}</p>
          {meta.amount && <p className="font-mono text-[#8E2BC4]">{money(meta.amount)}</p>}
          {m.body && <p className="mt-1 text-sm text-slate-300 whitespace-pre-line">{m.body}</p>}
        </div>
      )}
      {m.kind === "meeting" && (
        <div>
          <p className="kicker flex items-center gap-1.5"><CalendarClock className="h-3.5 w-3.5" />Meeting request</p>
          <p className="mt-2 text-[#2A1240]">{meta.title}</p>
          <p className="text-sm text-slate-300">{fmtDateTime(meta.when)} {meta.location && `· ${meta.location}`}</p>
          {m.body && <p className="mt-1 text-sm text-slate-300">{m.body}</p>}
        </div>
      )}
      {m.kind === "contract" && (
        <div>
          <p className="kicker flex items-center gap-1.5"><FileSignature className="h-3.5 w-3.5" />Contract</p>
          <p className="mt-2 text-[#2A1240]">{meta.title}</p>
          {meta.sign_token ? (
            <Link to={`/sign/${meta.sign_token}`} target="_blank" className="mt-2 inline-block text-sm text-[#8E2BC4] underline" data-testid={`contract-link-${m.id}`}>Review & sign</Link>
          ) : <p className="text-xs text-slate-400 mt-1">Not yet sent for signature</p>}
        </div>
      )}
      {(m.kind === "proposal" || m.kind === "meeting") && (
        actionable ? (
          <div className="mt-3 flex gap-2">
            <button className="btn-gold !py-1 !px-3 !text-xs" onClick={() => onRespond(m.id, "accepted")} data-testid={`accept-${m.id}`}><Check className="h-3 w-3" />Accept</button>
            <button className="btn-ghost !py-1 !px-3 !text-xs" onClick={() => onRespond(m.id, "declined")} data-testid={`decline-${m.id}`}><X className="h-3 w-3" />Decline</button>
          </div>
        ) : <p className={`mt-2 text-[11px] uppercase tracking-wider ${status === "accepted" ? "text-emerald-300" : status === "declined" ? "text-red-300" : "text-slate-400"}`}>{status}</p>
      )}
    </div>
  );
}

function Composer({ tid, onSent }) {
  const [text, setText] = useState("");
  const [mode, setMode] = useState(null);
  const [f, setF] = useState({});
  const [agreements, setAgreements] = useState([]);
  const fileRef = useRef(null);
  useEffect(() => {
    if (mode === "contract") api.get("/agreements").then((r) => setAgreements(r.data || [])).catch(() => {});
  }, [mode]);

  const send = async (kind, body, meta = {}) => {
    try {
      await api.post(`/messages/threads/${tid}`, { kind, body, meta });
      onSent();
      return true;
    } catch (e) {
      toast.error(errMsg(e));
      return false;
    }
  };
  const sendText = async () => {
    if (!text.trim()) return;
    if (await send("text", text)) setText("");
  };
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/messages/threads/${tid}/attachment`, fd);
      onSent();
    } catch (err) {
      toast.error(errMsg(err));
    }
    e.target.value = "";
  };
  const submitMode = async () => {
    let ok = false;
    if (mode === "proposal") ok = await send("proposal", f.body || "", { title: f.title, amount: Number(f.amount || 0) });
    if (mode === "meeting") ok = await send("meeting", f.body || "", { title: f.title, when: f.when, location: f.location || "" });
    if (mode === "contract") ok = await send("contract", "", { agreement_id: f.agreement_id });
    if (ok) {
      setMode(null);
      setF({});
    }
  };
  const tool = "grid h-10 w-10 place-items-center rounded-full border border-[#2A1240]/15 text-slate-200 hover:border-[#E0218A] hover:text-[#2A1240] transition-colors";
  return (
    <div className="border-t border-[#2A1240]/10 p-3 sm:p-4">
      <div className="flex flex-wrap sm:flex-nowrap items-end gap-2">
        <button className={tool} onClick={() => fileRef.current?.click()} aria-label="Attach file" data-testid="composer-attach"><Paperclip className="h-4 w-4" /></button>
        <input type="file" ref={fileRef} className="hidden" onChange={upload} data-testid="composer-file-input" />
        <button className={tool} onClick={() => setMode("proposal")} aria-label="Send proposal" data-testid="composer-proposal"><FileText className="h-4 w-4" /></button>
        <button className={tool} onClick={() => setMode("meeting")} aria-label="Request meeting" data-testid="composer-meeting"><CalendarClock className="h-4 w-4" /></button>
        <button className={tool} onClick={() => setMode("contract")} aria-label="Share contract" data-testid="composer-contract"><FileSignature className="h-4 w-4" /></button>
        <textarea rows={1} value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendText(); } }}
          placeholder="Write a message…" className="field order-first basis-full sm:basis-auto sm:order-none flex-1 !py-2.5 resize-none" data-testid="composer-input" />
        <button className="ml-auto sm:ml-0 grid h-10 w-10 place-items-center rounded-full bg-[#E0218A] text-white" onClick={sendText} aria-label="Send" data-testid="composer-send"><Send className="h-4 w-4" /></button>
      </div>
      <Dialog open={!!mode} onOpenChange={(o) => !o && setMode(null)}>
        <DialogContent className="bg-[#FFFFFF] border-[#2A1240]/10" data-testid="composer-dialog">
          <DialogTitle className="font-display text-2xl font-normal capitalize">{mode === "meeting" ? "Request a meeting" : mode === "contract" ? "Share a contract link" : "Send a proposal"}</DialogTitle>
          {mode !== "contract" && <input className="field" placeholder="Title" value={f.title || ""} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="composer-title-input" />}
          {mode === "proposal" && <input className="field" type="number" placeholder="Proposed value ($)" value={f.amount || ""} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="composer-amount-input" />}
          {mode === "meeting" && (
            <>
              <input className="field" type="datetime-local" value={f.when || ""} onChange={(e) => setF({ ...f, when: e.target.value })} data-testid="composer-when-input" />
              <input className="field" placeholder="Location or video link" value={f.location || ""} onChange={(e) => setF({ ...f, location: e.target.value })} data-testid="composer-location-input" />
            </>
          )}
          {mode === "contract" && (
            <select className="field" value={f.agreement_id || ""} onChange={(e) => setF({ ...f, agreement_id: e.target.value })} data-testid="composer-agreement-select">
              <option value="">Choose an agreement</option>
              {agreements.map((a) => <option key={a.id} value={a.id}>{a.title} ({a.status.replace("_", " ")})</option>)}
            </select>
          )}
          {mode !== "contract" && <textarea className="field" rows={3} placeholder={mode === "proposal" ? "Deliverables, timeline, terms" : "Agenda (optional)"} value={f.body || ""} onChange={(e) => setF({ ...f, body: e.target.value })} data-testid="composer-body-input" />}
          <button className="btn-gold" onClick={submitMode} disabled={mode === "contract" ? !f.agreement_id : !f.title || (mode === "meeting" && !f.when)} data-testid="composer-dialog-send">Send</button>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function Messages() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [sp, setSp] = useSearchParams();
  const tid = sp.get("thread");
  const [threads, setThreads] = useState([]);
  const [thread, setThread] = useState(null);
  const endRef = useRef(null);

  const loadThreads = () => api.get("/messages/threads").then((r) => setThreads(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  const loadThread = () => tid && api.get(`/messages/threads/${tid}`).then((r) => setThread(r.data)).catch(() => setThread(null));

  useEffect(() => {
    loadThreads();
    const t = setInterval(loadThreads, 8000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => {
    setThread(null);
    if (!tid) return undefined;
    loadThread();
    const t = setInterval(loadThread, 4000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tid]);
  const count = thread?.messages?.length || 0;
  useEffect(() => {
    endRef.current?.scrollIntoView({ block: "end" });
  }, [count]);

  const respond = async (id, status) => {
    await api.patch(`/messages/${id}`, { status }).catch((e) => toast.error(errMsg(e)));
    loadThread();
  };

  return (
    <div data-testid="messages-page">
      <h1 className="font-display text-3xl sm:text-4xl mb-6">Messages</h1>
      <div className="grid lg:grid-cols-12 rounded-2xl border border-[#2A1240]/10 bg-[#FFFFFF] overflow-hidden h-[calc(100vh-220px)] min-h-[480px]">
        <div className={`lg:col-span-4 border-r border-[#2A1240]/10 overflow-y-auto ${tid ? "hidden lg:block" : ""}`} data-lenis-prevent data-testid="thread-list">
          {!threads.length && <p className="p-6 text-sm text-slate-400">No conversations yet. Open a company in <Link to="/discover" className="text-[#8E2BC4] underline">Discover</Link> and press Contact.</p>}
          {threads.map((t) => (
            <button key={t.id} onClick={() => setSp({ thread: t.id })} data-testid={`thread-${t.id}`}
              className={`w-full flex items-center gap-3 p-4 text-left border-b border-[#2A1240]/5 hover:bg-[#2A1240]/5 transition-colors ${tid === t.id ? "bg-[#2A1240]/5" : ""}`}>
              <CompanyAvatar c={{ name: t.other?.name, logo_url: t.other?.logo }} size="h-11 w-11" text="text-base" />
              <div className="min-w-0 flex-1">
                <p className="text-sm text-[#2A1240] truncate">{t.other?.name} <span className="text-slate-400 text-xs">· {t.other?.type}</span></p>
                <p className="text-xs text-slate-400 truncate">{t.last_message || "Say hello"}</p>
              </div>
            </button>
          ))}
        </div>
        <div className={`lg:col-span-8 flex flex-col min-h-0 ${tid ? "" : "hidden lg:flex"}`}>
          {!tid ? (
            <div className="flex-1 grid place-items-center text-sm text-slate-400">Select a conversation</div>
          ) : (
            <>
              <div className="flex items-center gap-3 border-b border-[#2A1240]/10 p-4">
                <button className="lg:hidden" onClick={() => setSp({})} aria-label="Back" data-testid="thread-back"><ArrowLeft className="h-5 w-5" /></button>
                <p className="font-display text-xl">{thread?.other?.name || "…"}</p>
                {thread?.other_id && <button onClick={() => api.post("/deal-rooms", { company_id: thread.other_id }).then((r) => nav(`/app/deal-rooms/${r.data.id}`)).catch((e) => toast.error(errMsg(e)))} className="ml-auto rounded-full border border-[#E0218A]/40 px-3 py-1.5 text-xs text-[#8E2BC4] hover:bg-[#E0218A]/10" data-testid="open-deal-room-button">Deal room</button>}
                {thread?.other_id && <Link to={`/companies/${thread.other_id}`} className="text-xs text-slate-300 hover:text-[#2A1240]">View profile</Link>}
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-3" data-lenis-prevent data-testid="message-list">
                {(thread?.messages || []).map((m) => <Bubble key={m.id} m={m} mine={m.sender_company_id === user.company_id} onRespond={respond} />)}
                <div ref={endRef} />
              </div>
              <Composer tid={tid} onSent={() => { loadThread(); loadThreads(); }} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
